import { useEffect, useMemo, useState } from "react";
import { api, extractError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import {
  Calendar, Sparkles, Package, FileText, PhoneCall, AlertTriangle,
  ListChecks, ShieldCheck, Plus, Trash2, CheckCircle2, Circle, User as UserIcon, ExternalLink
} from "lucide-react";
import { Link } from "react-router-dom";
import { toISODate } from "@/lib/time";

const FR_DAYS = ["lundi", "mardi", "mercredi", "jeudi", "vendredi", "samedi", "dimanche"];
const FR_MONTHS = ["janvier", "février", "mars", "avril", "mai", "juin", "juillet", "août", "septembre", "octobre", "novembre", "décembre"];

function fmtDate(iso) {
  if (!iso) return "";
  const d = new Date(iso.length === 10 ? iso + "T00:00:00" : iso);
  return `${d.getDate()} ${FR_MONTHS[d.getMonth()]}`;
}

function WeekCalendar({ from, to, productions, loans, contrats }) {
  const start = new Date(from + "T00:00:00");
  const days = Array.from({ length: 7 }, (_, i) => {
    const d = new Date(start); d.setDate(d.getDate() + i);
    return d;
  });
  const today = toISODate(new Date());
  const eventsOn = (iso) => productions.filter(p => p.start_date === iso);
  const loansOn = (iso) => loans.filter(l => l.from_date <= iso && l.to_date >= iso);
  const contratsOn = (iso) => contrats.filter(c => c.start_date === iso);
  return (
    <div className="grid grid-cols-1 md:grid-cols-7 gap-2">
      {days.map(d => {
        const iso = toISODate(d);
        const isToday = iso === today;
        const evs = eventsOn(iso);
        const lns = loansOn(iso);
        const ctr = contratsOn(iso);
        return (
          <div key={iso} className={`rounded-xl border-2 p-2 min-h-[120px] ${isToday ? "border-primary bg-primary/10 shadow-glow" : "border-border/60 bg-card"}`} data-testid={`day-${iso}`}>
            <div className={`text-[10px] uppercase tracking-widest font-bold ${isToday ? "text-primary" : "text-muted-foreground"}`}>{FR_DAYS[d.getDay() === 0 ? 6 : d.getDay() - 1]}</div>
            <div className="text-lg font-brand">{d.getDate()} <span className="text-xs text-muted-foreground">{FR_MONTHS[d.getMonth()].slice(0, 3)}.</span></div>
            <div className="space-y-1 mt-1">
              {evs.map(e => (
                <Link key={e.id} to={`/evenements/${e.id}`} className="block truncate rounded-md bg-primary/15 text-primary text-[10px] font-semibold px-1.5 py-0.5 hover:bg-primary/25" title={e.name}>
                  🎉 {e.name}
                </Link>
              ))}
              {lns.map(l => (
                <div key={l.id} className="truncate rounded-md bg-secondary/40 text-[10px] font-semibold px-1.5 py-0.5" title={l.equipment?.name}>
                  📦 {l.equipment?.name || "—"}
                </div>
              ))}
              {ctr.map(c => (
                <div key={c.id} className="truncate rounded-md bg-fuchsia-500/15 text-fuchsia-200 text-[10px] font-semibold px-1.5 py-0.5" title={`${c.intermittent?.first_name} ${c.intermittent?.last_name || c.intermittent?.stage_name || ""}`}>
                  📝 {c.intermittent?.first_name} {(c.intermittent?.last_name || c.intermittent?.stage_name || "").slice(0, 12)}
                </div>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function Section({ icon: Icon, title, count, tint = "bg-primary/10 text-primary", children, testId }) {
  return (
    <Card className="border-border/60 shadow-warm" data-testid={testId}>
      <CardContent className="p-5">
        <div className="flex items-center gap-2 mb-3">
          <div className={`h-8 w-8 rounded-full grid place-items-center ${tint}`}><Icon className="h-4 w-4" /></div>
          <div className="font-brand text-lg">{title}</div>
          <Badge variant="outline" className="rounded-full ml-auto">{count}</Badge>
        </div>
        {children}
      </CardContent>
    </Card>
  );
}

function TodoBlock({ title, items, isAdminBlock, canEditText, onAdd, onToggle, onDelete, onEditText, testId }) {
  const [text, setText] = useState("");
  return (
    <Card className={`border ${isAdminBlock ? "border-primary/60" : "border-primary/50"} bg-card shadow-warm`} data-testid={testId}>
      <CardContent className="p-5">
        <div className="flex items-center gap-2 mb-2">
          <ListChecks className={`h-4 w-4 ${isAdminBlock ? "text-secondary-foreground" : "text-primary"}`} />
          <div className="font-brand text-lg">{title}</div>
          <Badge variant="outline" className="rounded-full ml-auto">{items.length}</Badge>
        </div>
        {canEditText && (
          <div className="flex gap-2 mb-3">
            <Input value={text} onChange={e => setText(e.target.value)} placeholder="Ajouter une tâche…" data-testid={`${testId}-input`} onKeyDown={e => { if (e.key === "Enter" && text.trim()) { onAdd(text); setText(""); } }} />
            <Button size="sm" className="rounded-full" onClick={() => { if (text.trim()) { onAdd(text); setText(""); } }}><Plus className="h-3.5 w-3.5" /></Button>
          </div>
        )}
        <div className="space-y-1.5">
          {items.length === 0 && <div className="text-xs italic text-muted-foreground py-2">Aucune tâche</div>}
          {items.map(t => (
            <div key={t.id} className={`flex items-center gap-2 rounded-lg border px-2 py-1.5 bg-card group ${t.done ? "opacity-60" : ""}`} data-testid={`${testId}-item-${t.id}`}>
              <button onClick={() => onToggle(t)} className="text-primary shrink-0" data-testid={`${testId}-toggle-${t.id}`}>
                {t.done ? <CheckCircle2 className="h-4 w-4 fill-primary text-white" /> : <Circle className="h-4 w-4" />}
              </button>
              <span className={`text-sm flex-1 ${t.done ? "line-through" : ""}`}>{t.text}</span>
              {canEditText && (
                <button onClick={() => onDelete(t)} className="text-muted-foreground hover:text-destructive opacity-0 group-hover:opacity-100 transition"><Trash2 className="h-3.5 w-3.5" /></button>
              )}
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

export default function DashboardPage() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [todos, setTodos] = useState([]);
  const [adminTodos, setAdminTodos] = useState([]);
  const isAdmin = user?.role === "admin";

  const load = async () => {
    try {
      const [d, t, at] = await Promise.all([
        api.get("/dashboard"),
        api.get("/todos"),
        api.get("/admin-todos"),
      ]);
      setData(d.data); setTodos(t.data); setAdminTodos(at.data);
    } catch (e) { toast.error(extractError(e)); }
  };
  useEffect(() => { load(); }, []);

  const addTodo = async (text) => { try { const { data: t } = await api.post("/todos", { text }); setTodos([t, ...todos]); } catch (e) { toast.error(extractError(e)); } };
  const toggleTodo = async (t) => { try { await api.patch(`/todos/${t.id}`, { done: !t.done }); setTodos(todos.map(x => x.id === t.id ? { ...x, done: !x.done } : x)); } catch (e) { toast.error(extractError(e)); } };
  const deleteTodo = async (t) => { if (!confirm("Supprimer cette tâche ?")) return; try { await api.delete(`/todos/${t.id}`); setTodos(todos.filter(x => x.id !== t.id)); } catch (e) { toast.error(extractError(e)); } };

  const addAdminTodo = async (text) => { try { const { data: t } = await api.post("/admin-todos", { text }); setAdminTodos([t, ...adminTodos]); } catch (e) { toast.error(extractError(e)); } };
  const toggleAdminTodo = async (t) => { try { await api.patch(`/admin-todos/${t.id}`, { done: !t.done }); setAdminTodos(adminTodos.map(x => x.id === t.id ? { ...x, done: !x.done } : x)); } catch (e) { toast.error(extractError(e)); } };
  const deleteAdminTodo = async (t) => { if (!confirm("Supprimer cette tâche ?")) return; try { await api.delete(`/admin-todos/${t.id}`); setAdminTodos(adminTodos.filter(x => x.id !== t.id)); } catch (e) { toast.error(extractError(e)); } };

  if (!data) return <div className="p-10 text-center text-muted-foreground">Chargement…</div>;
  const firstName = user?.name?.split(" ")[0] || "";

  return (
    <div className="space-y-8" data-testid="dashboard-page">
      {/* Bonjour */}
      <div className="text-center py-6">
        <h1 className="font-brand text-4xl sm:text-5xl lg:text-6xl">Bonjour <span className="text-primary">{firstName}</span> ! 👋</h1>
        <div className="text-xs uppercase tracking-widest text-muted-foreground font-bold mt-2">
          Semaine du {fmtDate(data.week.from)} au {fmtDate(data.week.to)}
        </div>
      </div>

      {/* Calendrier de la semaine */}
      <Card className="border-border/60 shadow-warm" data-testid="dash-calendar">
        <CardContent className="p-5">
          <div className="flex items-center gap-2 mb-3">
            <Calendar className="h-4 w-4 text-primary" />
            <div className="font-brand text-lg">Calendrier de la semaine</div>
          </div>
          <WeekCalendar from={data.week.from} to={data.week.to} productions={data.productions} loans={data.loans} contrats={data.contrats} />
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Section icon={Sparkles} title="Productions de la semaine" count={data.productions.length} testId="dash-productions">
          {data.productions.length === 0 && <div className="text-xs italic text-muted-foreground py-2">Aucune production</div>}
          {data.productions.map(e => (
            <Link key={e.id} to={`/evenements/${e.id}`} className="flex items-center gap-2 rounded-lg border border-border/50 bg-card p-2 hover:bg-muted mb-1.5" data-testid={`dash-prod-${e.id}`}>
              <Badge className="rounded-full text-[10px] bg-primary/20 text-primary border-primary/40">{fmtDate(e.start_date)}</Badge>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold truncate">{e.name}</div>
                <div className="text-[11px] text-muted-foreground truncate">{e.client || "—"} · {e.location || "—"}</div>
              </div>
              <Badge variant="outline" className="rounded-full text-[9px]">{e.phase}</Badge>
              <ExternalLink className="h-3 w-3 text-muted-foreground" />
            </Link>
          ))}
        </Section>

        <Section icon={Package} title="Prêts / Locations de la semaine" count={data.loans.length} tint="bg-secondary/40 text-secondary-foreground" testId="dash-loans">
          {data.loans.length === 0 && <div className="text-xs italic text-muted-foreground py-2">Aucun prêt en cours</div>}
          {data.loans.map(l => (
            <div key={l.id} className="flex items-center gap-2 rounded-lg border border-border/50 bg-card p-2 mb-1.5">
              <Badge className="rounded-full text-[10px] bg-secondary/40">{fmtDate(l.from_date)} → {fmtDate(l.to_date)}</Badge>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold truncate">{l.equipment?.name || "—"}</div>
                <div className="text-[11px] text-muted-foreground truncate">{l.borrower_name || "—"}</div>
              </div>
            </div>
          ))}
        </Section>

        <Section icon={FileText} title="Contrats à faire dans la semaine" count={data.contrats.length} tint="bg-fuchsia-500/15 text-fuchsia-200" testId="dash-contrats">
          {data.contrats.length === 0 && <div className="text-xs italic text-muted-foreground py-2">Aucun contrat urgent</div>}
          {data.contrats.map(c => (
            <Link key={c.id} to="/declarations" className="flex items-center gap-2 rounded-lg border border-border/50 bg-card p-2 hover:bg-muted mb-1.5">
              <Badge className="rounded-full text-[10px] bg-fuchsia-500/20 text-fuchsia-200">{fmtDate(c.start_date)}</Badge>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold truncate">{c.intermittent?.first_name} {c.intermittent?.last_name || c.intermittent?.stage_name}</div>
                <div className="text-[11px] text-muted-foreground truncate">{c.role || "—"} · {c.event_name || "—"}</div>
              </div>
              <ExternalLink className="h-3 w-3 text-muted-foreground" />
            </Link>
          ))}
        </Section>

        <Section icon={AlertTriangle} title="Priorités de la semaine" count={data.priorites.length} tint="bg-amber-500/15 text-amber-200" testId="dash-priorites">
          <div className="text-[11px] text-muted-foreground mb-2">Devis à remettre cette semaine (référence : date de demande client)</div>
          {data.priorites.length === 0 && <div className="text-xs italic text-muted-foreground py-2">Rien de bouillant 😌</div>}
          {data.priorites.map(p => (
            <Link key={p.event_id} to={`/evenements/${p.event_id}`} className="flex items-center gap-2 rounded-lg border border-amber-500/50 bg-amber-500/15 p-2 hover:bg-amber-500/15 mb-1.5" data-testid={`dash-prio-${p.event_id}`}>
              <Badge className="rounded-full text-[10px] bg-amber-500 text-white">DÛ {fmtDate(p.due_date)}</Badge>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold truncate">{p.name}</div>
                <div className="text-[11px] text-muted-foreground truncate">{p.client || "—"} · Event {fmtDate(p.start_date)}</div>
              </div>
              {p.lead && <Badge variant="outline" className="rounded-full text-[9px]"><UserIcon className="h-2.5 w-2.5 mr-0.5" /> {p.lead}</Badge>}
            </Link>
          ))}
        </Section>

        <Section icon={PhoneCall} title="Relances devis (> 7j sans réponse)" count={data.relances_devis.length} tint="bg-orange-500/15 text-orange-200" testId="dash-relances-devis">
          {data.relances_devis.length === 0 && <div className="text-xs italic text-muted-foreground py-2">Aucune relance</div>}
          {data.relances_devis.map(r => (
            <Link key={r.event_id} to={`/evenements/${r.event_id}`} className="flex items-center gap-2 rounded-lg border border-orange-500/50 bg-orange-500/15 p-2 hover:bg-orange-500/15 mb-1.5" data-testid={`dash-relance-devis-${r.event_id}`}>
              <Badge className="rounded-full text-[10px] bg-orange-500 text-white">+{r.days}j</Badge>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold truncate">{r.name}</div>
                <div className="text-[11px] text-muted-foreground truncate">{r.client || "—"} · {r.amount ? `${Number(r.amount).toLocaleString("fr-FR")} €` : ""}</div>
              </div>
              {r.lead && <Badge variant="outline" className="rounded-full text-[9px]">{r.lead}</Badge>}
            </Link>
          ))}
        </Section>

        <Section icon={PhoneCall} title="Relances facture (> 30j impayée)" count={data.relances_facture.length} tint="bg-rose-500/15 text-rose-200" testId="dash-relances-facture">
          {data.relances_facture.length === 0 && <div className="text-xs italic text-muted-foreground py-2">Aucune relance</div>}
          {data.relances_facture.map(r => (
            <Link key={r.event_id} to={`/evenements/${r.event_id}`} className="flex items-center gap-2 rounded-lg border border-rose-500/50 bg-rose-500/15 p-2 hover:bg-rose-500/15 mb-1.5">
              <Badge className="rounded-full text-[10px] bg-rose-500 text-white">+{r.days}j</Badge>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold truncate">{r.name}</div>
                <div className="text-[11px] text-muted-foreground truncate">{r.client || "—"} · {r.amount ? `${Number(r.amount).toLocaleString("fr-FR")} €` : ""}</div>
              </div>
              {r.lead && <Badge variant="outline" className="rounded-full text-[9px]">{r.lead}</Badge>}
            </Link>
          ))}
        </Section>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <TodoBlock
          title="Ma to-do personnelle"
          items={todos}
          canEditText={true}
          onAdd={addTodo} onToggle={toggleTodo} onDelete={deleteTodo}
          testId="dash-todos"
        />
        <TodoBlock
          title="To-do administrateur"
          items={adminTodos}
          isAdminBlock={true}
          canEditText={isAdmin}
          onAdd={addAdminTodo} onToggle={toggleAdminTodo} onDelete={deleteAdminTodo}
          testId="dash-admin-todos"
        />
      </div>
    </div>
  );
}
